import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Building2,
  Users,
  Plus,
  Trash2,
  Loader2,
  ClipboardList,
  Settings,
  Eye,
  BarChart3,
  Crown,
  Globe,
  CheckCircle2,
  AlertTriangle,
  Pause,
  Edit3,
  Save,
  Key,
  Copy,
  RefreshCw,
  ExternalLink,
  Mail,
  User,
  DollarSign,
  CreditCard,
  TrendingUp,
  Clock,
  FileText,
  Receipt,
  PlusCircle,
  Gift,
  Calendar,
  Hash,
  X
} from 'lucide-react';
import Layout from '../../components/Layout';
import api from '../../lib/api';

const SuperAdminPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [companyStats, setCompanyStats] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState(null);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Parse tab from URL query params
  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'billing') return 'billing';
    if (tab === 'cleanup') return 'cleanup';
    return 'companies';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromUrl());
  const [billingOverview, setBillingOverview] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [billingDetailsOpen, setBillingDetailsOpen] = useState(false);
  const [selectedBillingCompany, setSelectedBillingCompany] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [loadingBillingDetails, setLoadingBillingDetails] = useState(false);
  const [billingNotes, setBillingNotes] = useState('');
  const [savingBillingNotes, setSavingBillingNotes] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [addingCredit, setAddingCredit] = useState(false);
  const [billingTab, setBillingTab] = useState('overview');
  const [orphanedData, setOrphanedData] = useState(null);
  const [loadingOrphaned, setLoadingOrphaned] = useState(false);
  const [deletingOrphaned, setDeletingOrphaned] = useState(false);
  
  // Helper function to format relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return { text: 'Never', color: 'text-slate-500', bgColor: 'bg-slate-500/10' };
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    let text, color, bgColor;
    
    if (diffMins < 1) {
      text = 'Just now';
      color = 'text-green-400';
      bgColor = 'bg-green-500/10';
    } else if (diffMins < 60) {
      text = `${diffMins}m ago`;
      color = 'text-green-400';
      bgColor = 'bg-green-500/10';
    } else if (diffHours < 24) {
      text = `${diffHours}h ago`;
      color = 'text-green-400';
      bgColor = 'bg-green-500/10';
    } else if (diffDays < 7) {
      text = `${diffDays}d ago`;
      color = diffDays <= 3 ? 'text-yellow-400' : 'text-orange-400';
      bgColor = diffDays <= 3 ? 'bg-yellow-500/10' : 'bg-orange-500/10';
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      text = `${weeks}w ago`;
      color = 'text-red-400';
      bgColor = 'bg-red-500/10';
    } else {
      const months = Math.floor(diffDays / 30);
      text = `${months}mo ago`;
      color = 'text-red-400';
      bgColor = 'bg-red-500/10';
    }
    
    return { text, color, bgColor };
  };
  
  const [newCompany, setNewCompany] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
    logo_url: '',
    primary_color: '#F59E0B',
    secondary_color: '#1E293B',
    max_users: 50,
    plan: 'trial'
  });

  useEffect(() => {
    fetchCompanies();
     
  }, []);

  // Handle URL tab parameter changes
  useEffect(() => {
    const newTab = getTabFromUrl();
    setActiveTab(newTab);
    
    // Auto-load data for the tab
    if (newTab === 'billing' && !billingOverview) {
      fetchBillingOverview();
    } else if (newTab === 'cleanup' && !orphanedData) {
      fetchOrphanedData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingOverview = async () => {
    try {
      setLoadingBilling(true);
      const response = await api.get('/admin/billing/overview');
      setBillingOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch billing overview:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleUpdatePlan = async (companyId, plan) => {
    try {
      await api.post(`/admin/billing/update-plan/${companyId}?plan=${plan}`);
      toast.success('Plan updated successfully');
      fetchBillingOverview();
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const fetchBillingDetails = async (companyId) => {
    try {
      setLoadingBillingDetails(true);
      const response = await api.get(`/admin/billing/company/${companyId}`);
      setBillingDetails(response.data);
      setBillingNotes(response.data.billing?.notes || '');
    } catch (error) {
      console.error('Failed to fetch billing details:', error);
      toast.error('Failed to load billing details');
    } finally {
      setLoadingBillingDetails(false);
    }
  };

  const handleViewBillingDetails = async (company) => {
    setSelectedBillingCompany(company);
    setBillingDetailsOpen(true);
    setBillingTab('overview');
    await fetchBillingDetails(company.company_id);
  };

  const handleSaveBillingNotes = async () => {
    if (!selectedBillingCompany) return;
    try {
      setSavingBillingNotes(true);
      await api.patch(`/admin/billing/company/${selectedBillingCompany.company_id}?notes=${encodeURIComponent(billingNotes)}`);
      toast.success('Notes saved successfully');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingBillingNotes(false);
    }
  };

  const handleAddCredit = async () => {
    if (!selectedBillingCompany || !creditAmount || !creditDescription) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      setAddingCredit(true);
      await api.post(`/admin/billing/company/${selectedBillingCompany.company_id}/credit?amount=${creditAmount}&description=${encodeURIComponent(creditDescription)}`);
      toast.success(`Credit of $${creditAmount} added`);
      setCreditDialogOpen(false);
      setCreditAmount('');
      setCreditDescription('');
      await fetchBillingDetails(selectedBillingCompany.company_id);
    } catch (error) {
      toast.error('Failed to add credit');
    } finally {
      setAddingCredit(false);
    }
  };

  const handleToggleComped = async (isComped) => {
    if (!selectedBillingCompany) return;
    try {
      await api.patch(`/admin/billing/company/${selectedBillingCompany.company_id}?is_comped=${isComped}`);
      toast.success(isComped ? 'Account marked as complimentary' : 'Complimentary status removed');
      await fetchBillingDetails(selectedBillingCompany.company_id);
      fetchBillingOverview();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const fetchOrphanedData = async () => {
    try {
      setLoadingOrphaned(true);
      const response = await api.get('/admin/orphaned-data');
      setOrphanedData(response.data);
    } catch (error) {
      console.error('Failed to fetch orphaned data:', error);
      toast.error('Failed to load orphaned data');
    } finally {
      setLoadingOrphaned(false);
    }
  };

  const handleDeleteOrphanedData = async () => {
    try {
      setDeletingOrphaned(true);
      const response = await api.delete('/admin/orphaned-data');
      toast.success(response.data.message);
      setOrphanedData(null);
      fetchOrphanedData();
    } catch (error) {
      toast.error('Failed to delete orphaned data');
    } finally {
      setDeletingOrphaned(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    
    if (!newCompany.name || !newCompany.slug || !newCompany.admin_email || !newCompany.admin_password || !newCompany.admin_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(newCompany.slug)) {
      toast.error('Slug must contain only lowercase letters, numbers, and hyphens');
      return;
    }
    
    try {
      setCreating(true);
      await api.post('/companies', newCompany);
      toast.success(`Company "${newCompany.name}" created successfully!`);
      setDialogOpen(false);
      setNewCompany({
        name: '',
        slug: '',
        admin_email: '',
        admin_password: '',
        admin_name: '',
        logo_url: '',
        primary_color: '#F59E0B',
        secondary_color: '#1E293B',
        max_users: 50,
        plan: 'trial'
      });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCompany = async (company) => {
    try {
      await api.delete(`/companies/${company.id}`);
      toast.success(`Company "${company.name}" deleted`);
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const handleViewStats = async (company) => {
    try {
      const response = await api.get(`/companies/${company.id}/stats`);
      setCompanyStats(response.data);
      setSelectedCompany(company);
      setStatsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load company stats');
    }
  };

  const handleToggleStatus = async (company) => {
    const newStatus = company.status === 'active' ? 'suspended' : 'active';
    try {
      await api.patch(`/companies/${company.id}`, { status: newStatus });
      toast.success(`Company status updated to ${newStatus}`);
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to update company status');
    }
  };

  const handleEditCompany = async (company) => {
    setEditingCompany({
      id: company.id,
      name: company.name,
      status: company.status,
      plan: company.plan,
      max_users: company.max_users,
      logo_url: company.logo_url || '',
      primary_color: company.primary_color || '#F59E0B',
      secondary_color: company.secondary_color || '#1E293B'
    });
    setGeneratedPassword(null);
    setEditDialogOpen(true);
    
    // Fetch admin credentials
    try {
      setLoadingCredentials(true);
      const response = await api.get(`/companies/${company.id}/admin-credentials`);
      setAdminCredentials(response.data);
    } catch (error) {
      console.error('Failed to fetch admin credentials:', error);
      setAdminCredentials(null);
    } finally {
      setLoadingCredentials(false);
    }
  };

  const handleResetAdminPassword = async () => {
    if (!editingCompany) return;
    
    try {
      setResettingPassword(true);
      const response = await api.post(`/companies/${editingCompany.id}/reset-admin-password`);
      setGeneratedPassword(response.data.new_password);
      setAdminCredentials({
        ...adminCredentials,
        admin_email: response.data.admin_email,
        admin_name: response.data.admin_name
      });
      toast.success('New password generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getLoginUrl = () => {
    return window.location.origin;
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    if (!editingCompany) return;
    
    try {
      setSaving(true);
      await api.patch(`/companies/${editingCompany.id}`, {
        name: editingCompany.name,
        status: editingCompany.status,
        plan: editingCompany.plan,
        max_users: editingCompany.max_users,
        logo_url: editingCompany.logo_url,
        primary_color: editingCompany.primary_color,
        secondary_color: editingCompany.secondary_color
      });
      toast.success('Company updated successfully');
      setEditDialogOpen(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="flex items-center gap-1 text-green-400 bg-green-500/20 px-2 py-1 rounded-sm text-xs"><CheckCircle2 className="w-3 h-3" /> Active</span>;
      case 'suspended':
        return <span className="flex items-center gap-1 text-red-400 bg-red-500/20 px-2 py-1 rounded-sm text-xs"><Pause className="w-3 h-3" /> Suspended</span>;
      case 'trial':
        return <span className="flex items-center gap-1 text-amber-400 bg-amber-500/20 px-2 py-1 rounded-sm text-xs"><AlertTriangle className="w-3 h-3" /> Trial</span>;
      default:
        return <span className="text-slate-400 text-xs">{status}</span>;
    }
  };

  const getPlanBadge = (plan) => {
    const colors = {
      trial: 'bg-slate-500/20 text-slate-400',
      basic: 'bg-blue-500/20 text-blue-400',
      pro: 'bg-purple-500/20 text-purple-400',
      enterprise: 'bg-amber-500/20 text-amber-400'
    };
    return <span className={`px-2 py-1 rounded-sm text-xs uppercase ${colors[plan] || colors.trial}`}>{plan}</span>;
  };

  // Check if user is super admin
  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <Crown className="w-16 h-16 text-amber-500 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Super Admin Access Required</h1>
          <p className="text-slate-400">You don't have permission to access this page.</p>
          <Button onClick={() => navigate('/dashboard')} className="btn-primary mt-4">
            Go to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
              <Crown className="w-10 h-10 text-amber-500" />
              Super Admin Panel
            </h1>
            <p className="text-slate-400 mt-1">Manage all companies and their data</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={() => navigate('/admin/landing-editor')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-medium flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              Edit Landing Page
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary flex items-center gap-2" data-testid="create-company-btn">
                <Plus className="w-5 h-5" />
                Create Company
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-amber-500" />
                  Create New Company
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCompany} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-400">Company Name *</Label>
                    <Input
                      value={newCompany.name}
                      onChange={(e) => {
                        setNewCompany({ 
                          ...newCompany, 
                          name: e.target.value,
                          slug: generateSlug(e.target.value)
                        });
                      }}
                      placeholder="Acme Corporation"
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="company-name-input"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-slate-400">URL Slug *</Label>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-500" />
                      <Input
                        value={newCompany.slug}
                        onChange={(e) => setNewCompany({ ...newCompany, slug: e.target.value.toLowerCase() })}
                        placeholder="acme-corp"
                        className="bg-slate-900 border-slate-600 text-white"
                        data-testid="company-slug-input"
                      />
                    </div>
                    <p className="text-slate-500 text-xs mt-1">Used for subdomain: {newCompany.slug || 'company'}.yourapp.com</p>
                  </div>
                  
                  <div className="col-span-2 border-t border-slate-700 pt-4">
                    <p className="text-slate-300 font-medium mb-3">Company Admin Account</p>
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-slate-400">Admin Name *</Label>
                    <Input
                      value={newCompany.admin_name}
                      onChange={(e) => setNewCompany({ ...newCompany, admin_name: e.target.value })}
                      placeholder="John Smith"
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="admin-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Admin Email *</Label>
                    <Input
                      type="email"
                      value={newCompany.admin_email}
                      onChange={(e) => setNewCompany({ ...newCompany, admin_email: e.target.value })}
                      placeholder="admin@acme.com"
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="admin-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Admin Password *</Label>
                    <Input
                      type="password"
                      value={newCompany.admin_password}
                      onChange={(e) => setNewCompany({ ...newCompany, admin_password: e.target.value })}
                      placeholder="••••••••"
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="admin-password-input"
                    />
                  </div>
                  
                  <div className="col-span-2 border-t border-slate-700 pt-4">
                    <p className="text-slate-300 font-medium mb-3">Plan & Limits</p>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Plan</Label>
                    <select
                      value={newCompany.plan}
                      onChange={(e) => setNewCompany({ ...newCompany, plan: e.target.value })}
                      className="w-full h-10 rounded-sm bg-slate-900 border border-slate-600 text-white px-3"
                      data-testid="plan-select"
                    >
                      <option value="trial">Trial</option>
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Max Users</Label>
                    <Input
                      type="number"
                      value={newCompany.max_users}
                      onChange={(e) => setNewCompany({ ...newCompany, max_users: parseInt(e.target.value) || 50 })}
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="max-users-input"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="btn-secondary">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} className="btn-primary" data-testid="submit-company-btn">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Company
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-slate-700">
          <button
            onClick={() => {
              setActiveTab('companies');
              fetchCompanies(); // Refresh companies list
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'companies' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Companies
          </button>
          <button
            onClick={() => {
              setActiveTab('billing');
              if (!billingOverview) fetchBillingOverview();
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'billing' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Billing
          </button>
          <button
            onClick={() => {
              setActiveTab('cleanup');
              if (!orphanedData) fetchOrphanedData();
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'cleanup' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Trash2 className="w-4 h-4 inline mr-2" />
            Data Cleanup
          </button>
        </div>

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <>
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-industrial">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase">Companies</p>
                <p className="text-2xl font-bold text-white">{companies.length}</p>
              </div>
            </div>
          </div>
          
          <div className="card-industrial">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase">Active</p>
                <p className="text-2xl font-bold text-green-400">{companies.filter(c => c.status === 'active').length}</p>
              </div>
            </div>
          </div>
          
          <div className="card-industrial">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-sm flex items-center justify-center">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase">Total Users</p>
                <p className="text-2xl font-bold text-cyan-400">{companies.reduce((sum, c) => sum + (c.user_count || 0), 0)}</p>
              </div>
            </div>
          </div>
          
          <div className="card-industrial">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase">Work Orders</p>
                <p className="text-2xl font-bold text-purple-400">{companies.reduce((sum, c) => sum + (c.work_order_count || 0), 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Companies List */}
        <div className="card-industrial">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-500" />
              All Companies
            </h2>
            <Button
              onClick={fetchCompanies}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No companies yet</p>
              <p className="text-slate-500 text-sm">Create your first company to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-slate-400 text-xs uppercase py-3 px-2">Company</th>
                    <th className="text-left text-slate-400 text-xs uppercase py-3 px-2">Slug</th>
                    <th className="text-left text-slate-400 text-xs uppercase py-3 px-2">Status</th>
                    <th className="text-left text-slate-400 text-xs uppercase py-3 px-2">Plan</th>
                    <th className="text-center text-slate-400 text-xs uppercase py-3 px-2">Users</th>
                    <th className="text-center text-slate-400 text-xs uppercase py-3 px-2">Work Orders</th>
                    <th className="text-center text-slate-400 text-xs uppercase py-3 px-2">Last Activity</th>
                    <th className="text-right text-slate-400 text-xs uppercase py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr key={company.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-sm flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: company.primary_color || '#F59E0B' }}
                          >
                            {company.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-medium">{company.name}</p>
                            <p className="text-slate-500 text-xs">Created {new Date(company.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <code className="text-cyan-400 bg-slate-900 px-2 py-1 rounded text-sm">{company.slug}</code>
                      </td>
                      <td className="py-3 px-2">
                        {getStatusBadge(company.status)}
                      </td>
                      <td className="py-3 px-2">
                        {getPlanBadge(company.plan)}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-white font-mono">{company.user_count || 0}</span>
                        <span className="text-slate-500 text-xs">/{company.max_users}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-white font-mono">{company.work_order_count || 0}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {(() => {
                          const activity = getRelativeTime(company.last_activity_at);
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${activity.color} ${activity.bgColor}`}>
                              <Clock className="w-3 h-3" />
                              {activity.text}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewStats(company)}
                            className="text-slate-400 hover:text-cyan-400 p-2"
                            title="View Stats"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCompany(company)}
                            className="text-slate-400 hover:text-amber-400 p-2"
                            title="Edit Company"
                            data-testid={`edit-company-${company.id}`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(company)}
                            className={`p-2 ${company.status === 'active' ? 'text-slate-400 hover:text-amber-400' : 'text-slate-400 hover:text-green-400'}`}
                            title={company.status === 'active' ? 'Suspend' : 'Activate'}
                          >
                            {company.status === 'active' ? <Pause className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-red-400 p-2"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-800 border-slate-700">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Delete Company?</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-400">
                                  This will permanently delete <strong className="text-white">{company.name}</strong> and ALL its data including users, work orders, inventory, and settings. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteCompany(company)}
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                  Delete Company
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <>
            {loadingBilling ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : billingOverview ? (
              <>
                {/* Billing Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="card-industrial">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs uppercase">Monthly Revenue</p>
                        <p className="text-2xl font-bold text-green-400">${billingOverview.total_mrr.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-industrial">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-cyan-500/20 rounded-sm flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs uppercase">Active Subscriptions</p>
                        <p className="text-2xl font-bold text-cyan-400">{billingOverview.active_subscriptions}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-industrial">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/20 rounded-sm flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs uppercase">On Trial</p>
                        <p className="text-2xl font-bold text-amber-400">{billingOverview.trial_companies}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-industrial">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs uppercase">Conversion Rate</p>
                        <p className="text-2xl font-bold text-purple-400">
                          {billingOverview.total_companies > 0 
                            ? ((billingOverview.active_subscriptions / billingOverview.total_companies) * 100).toFixed(0) 
                            : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Billing Table */}
                <div className="card-industrial">
                  <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-amber-500" />
                    Company Billing Status
                  </h2>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left py-3 px-2 text-slate-400 text-xs uppercase">Company</th>
                          <th className="text-left py-3 px-2 text-slate-400 text-xs uppercase">Plan</th>
                          <th className="text-left py-3 px-2 text-slate-400 text-xs uppercase">Price</th>
                          <th className="text-left py-3 px-2 text-slate-400 text-xs uppercase">Status</th>
                          <th className="text-left py-3 px-2 text-slate-400 text-xs uppercase">Next Billing / Trial Ends</th>
                          <th className="text-right py-3 px-2 text-slate-400 text-xs uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingOverview.companies.map((company) => (
                          <tr key={company.company_id} className="border-b border-slate-700/50">
                            <td className="py-3 px-2 text-white font-medium">{company.company_name}</td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded-sm text-xs uppercase ${
                                company.plan === 'enterprise' ? 'bg-amber-500/20 text-amber-400' :
                                company.plan === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                                company.plan === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {company.plan_name}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-white">${company.price}/mo</td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded-sm text-xs ${
                                company.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                company.status === 'trial' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {company.status.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-slate-400 text-sm">
                              {company.current_period_end 
                                ? new Date(company.current_period_end).toLocaleDateString()
                                : company.trial_ends_at 
                                  ? `Trial ends: ${new Date(company.trial_ends_at).toLocaleDateString()}`
                                  : '-'
                              }
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewBillingDetails(company)}
                                  className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 p-2"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <select
                                  value={company.plan}
                                  onChange={(e) => handleUpdatePlan(company.company_id, e.target.value)}
                                  className="bg-slate-900 border border-slate-600 text-white text-xs rounded px-2 py-1"
                                >
                                  <option value="free">Free (Comped)</option>
                                  <option value="trial">Trial</option>
                                  <option value="basic">Basic</option>
                                  <option value="pro">Pro</option>
                                  <option value="enterprise">Enterprise</option>
                                </select>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Transactions */}
                {billingOverview.recent_transactions?.length > 0 && (
                  <div className="card-industrial">
                    <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-amber-500" />
                      Recent Transactions
                    </h2>
                    
                    <div className="space-y-2">
                      {billingOverview.recent_transactions.map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-900 rounded-sm">
                          <div>
                            <p className="text-white font-medium">{tx.description}</p>
                            <p className="text-slate-400 text-sm">{new Date(tx.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-400 font-bold">${tx.amount.toFixed(2)}</p>
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">{tx.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No billing data available</p>
              </div>
            )}
          </>
        )}

        {/* Data Cleanup Tab */}
        {activeTab === 'cleanup' && (
          <>
            <div className="card-industrial mb-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Orphaned Data Cleanup
              </h2>
              <p className="text-slate-400 mb-4">
                Orphaned data are records that don't belong to any company. This can happen from old imports or data migration issues.
                Use this tool to clean up such records.
              </p>
              
              {loadingOrphaned ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                </div>
              ) : orphanedData ? (
                <div className="space-y-4">
                  {/* Orphaned Data Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-900 p-4 rounded-sm">
                      <p className="text-slate-400 text-xs uppercase">Work Orders</p>
                      <p className="text-2xl font-bold text-red-400">{orphanedData.orphaned_counts.tasks}</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-sm">
                      <p className="text-slate-400 text-xs uppercase">Users</p>
                      <p className="text-2xl font-bold text-red-400">{orphanedData.orphaned_counts.users}</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-sm">
                      <p className="text-slate-400 text-xs uppercase">Categories</p>
                      <p className="text-2xl font-bold text-red-400">{orphanedData.orphaned_counts.categories}</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-sm">
                      <p className="text-slate-400 text-xs uppercase">Locations</p>
                      <p className="text-2xl font-bold text-red-400">
                        {orphanedData.orphaned_counts.buildings + orphanedData.orphaned_counts.floors + orphanedData.orphaned_counts.places}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-900 p-4 rounded-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Total Orphaned Records</p>
                        <p className="text-slate-400 text-sm">These records are not associated with any company</p>
                      </div>
                      <p className="text-3xl font-bold text-red-400">{orphanedData.total}</p>
                    </div>
                  </div>
                  
                  {orphanedData.total > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button className="bg-red-600 hover:bg-red-700 w-full">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete All Orphaned Data ({orphanedData.total} records)
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-800 border-slate-700">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Orphaned Data?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            This will permanently delete {orphanedData.total} orphaned records including:
                            <ul className="list-disc list-inside mt-2">
                              <li>{orphanedData.orphaned_counts.tasks} work orders</li>
                              <li>{orphanedData.orphaned_counts.users} users</li>
                              <li>{orphanedData.orphaned_counts.categories} categories</li>
                              <li>{orphanedData.orphaned_counts.buildings + orphanedData.orphaned_counts.floors + orphanedData.orphaned_counts.places} locations</li>
                              <li>{orphanedData.orphaned_counts.inventory} inventory items</li>
                            </ul>
                            <p className="mt-2 text-red-400">This action cannot be undone!</p>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleDeleteOrphanedData}
                            disabled={deletingOrphaned}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {deletingOrphaned ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                            Delete All
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  
                  {orphanedData.total === 0 && (
                    <div className="bg-green-900/20 border border-green-600/30 p-4 rounded-sm text-center">
                      <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <p className="text-green-400 font-medium">No orphaned data found!</p>
                      <p className="text-slate-400 text-sm">All records are properly associated with companies.</p>
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    onClick={fetchOrphanedData}
                    className="btn-secondary"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Failed to load orphaned data</p>
                  <Button 
                    variant="outline" 
                    onClick={fetchOrphanedData}
                    className="btn-secondary mt-4"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Stats Dialog */}
        <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-500" />
                {selectedCompany?.name} - Statistics
              </DialogTitle>
            </DialogHeader>
            {companyStats && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Users</p>
                  <p className="text-2xl font-bold text-white">{companyStats.users}</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Work Orders</p>
                  <p className="text-2xl font-bold text-white">{companyStats.work_orders}</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Completed</p>
                  <p className="text-2xl font-bold text-green-400">{companyStats.completed_work_orders}</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Pending</p>
                  <p className="text-2xl font-bold text-amber-400">{companyStats.pending_work_orders}</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Locations</p>
                  <p className="text-2xl font-bold text-white">{companyStats.locations}</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Categories</p>
                  <p className="text-2xl font-bold text-white">{companyStats.categories}</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Inventory Items</p>
                  <p className="text-2xl font-bold text-white">{companyStats.inventory_items}</p>
                </div>
                <div className="bg-slate-900 p-4 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase">Time Logs</p>
                  <p className="text-2xl font-bold text-white">{companyStats.time_logs}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Company Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setGeneratedPassword(null);
            setAdminCredentials(null);
          }
        }}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-amber-500" />
                Edit Company
              </DialogTitle>
            </DialogHeader>
            {editingCompany && (
              <div className="space-y-6">
                {/* Admin Login Credentials Section */}
                <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-2 mb-4">
                    <Key className="w-5 h-5 text-cyan-500" />
                    <h3 className="text-white font-medium">Admin Login Credentials</h3>
                    <span className="text-xs text-slate-400 ml-auto">Share with client</span>
                  </div>
                  
                  {loadingCredentials ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      <span className="ml-2 text-slate-400">Loading credentials...</span>
                    </div>
                  ) : adminCredentials?.has_admin ? (
                    <div className="space-y-3">
                      {/* Login URL */}
                      <div className="flex items-center gap-2 p-2 bg-slate-800 rounded">
                        <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-400">Login URL</p>
                          <p className="text-white text-sm truncate">{getLoginUrl()}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(getLoginUrl(), 'Login URL')}
                          className="text-slate-400 hover:text-white shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Company Portal URL */}
                      <div className="flex items-center gap-2 p-2 bg-cyan-900/30 border border-cyan-600/30 rounded">
                        <Globe className="w-4 h-4 text-cyan-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-cyan-400">Company Portal URL</p>
                          <p className="text-white text-sm truncate">{getLoginUrl()}/login/{editingCompany?.id ? companies.find(c => c.id === editingCompany.id)?.slug : ''}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const company = companies.find(c => c.id === editingCompany.id);
                            copyToClipboard(`${getLoginUrl()}/login/${company?.slug || ''}`, 'Portal URL');
                          }}
                          className="text-slate-400 hover:text-white shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Admin Name */}
                      <div className="flex items-center gap-2 p-2 bg-slate-800 rounded">
                        <User className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-400">Admin Name</p>
                          <p className="text-white text-sm">{adminCredentials.admin_name}</p>
                        </div>
                      </div>
                      
                      {/* Admin Email */}
                      <div className="flex items-center gap-2 p-2 bg-slate-800 rounded">
                        <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-400">Admin Email (Username)</p>
                          <p className="text-white text-sm">{adminCredentials.admin_email}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(adminCredentials.admin_email, 'Email')}
                          className="text-slate-400 hover:text-white shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Password Section */}
                      <div className="flex items-center gap-2 p-2 bg-slate-800 rounded">
                        <Key className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-400">Password</p>
                          {generatedPassword ? (
                            <p className="text-green-400 text-sm font-mono">{generatedPassword}</p>
                          ) : (
                            <p className="text-slate-500 text-sm italic">Generate a new password to share</p>
                          )}
                        </div>
                        {generatedPassword && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(generatedPassword, 'Password')}
                            className="text-slate-400 hover:text-white shrink-0"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Generate Password Button */}
                      <Button
                        type="button"
                        onClick={handleResetAdminPassword}
                        disabled={resettingPassword}
                        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                        data-testid="generate-password-btn"
                      >
                        {resettingPassword ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        {generatedPassword ? 'Generate New Password' : 'Generate Password to Share'}
                      </Button>
                      
                      {generatedPassword && (
                        <div className="mt-3 p-3 bg-amber-900/30 border border-amber-600/50 rounded">
                          <p className="text-amber-400 text-xs">
                            <strong>Important:</strong> Copy this password now. For security reasons, it won't be shown again after you close this dialog.
                          </p>
                        </div>
                      )}
                      
                      {/* Copy All Button */}
                      {generatedPassword && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const company = companies.find(c => c.id === editingCompany.id);
                            const portalUrl = `${getLoginUrl()}/login/${company?.slug || ''}`;
                            const message = `Login Credentials for ${editingCompany.name}

Company Portal URL: ${portalUrl}
(or use main login: ${getLoginUrl()})

Email: ${adminCredentials.admin_email}
Password: ${generatedPassword}

Please change your password after first login.`;
                            copyToClipboard(message, 'All credentials');
                          }}
                          className="w-full btn-secondary"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy All Credentials
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-slate-400">No admin user found for this company</p>
                    </div>
                  )}
                </div>

                {/* Company Settings Form */}
                <form onSubmit={handleSaveCompany} className="space-y-4">
                  <div className="border-t border-slate-700 pt-4">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-slate-400" />
                      Company Settings
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-400">Company Name</Label>
                    <Input
                      value={editingCompany.name}
                      onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="edit-company-name"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Status</Label>
                    <select
                      value={editingCompany.status}
                      onChange={(e) => setEditingCompany({ ...editingCompany, status: e.target.value })}
                      className="w-full h-10 rounded-sm bg-slate-900 border border-slate-600 text-white px-3"
                      data-testid="edit-company-status"
                    >
                      <option value="trial">Trial</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Plan</Label>
                    <select
                      value={editingCompany.plan}
                      onChange={(e) => setEditingCompany({ ...editingCompany, plan: e.target.value })}
                      className="w-full h-10 rounded-sm bg-slate-900 border border-slate-600 text-white px-3"
                      data-testid="edit-company-plan"
                    >
                      <option value="trial">Trial</option>
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Max Users</Label>
                    <Input
                      type="number"
                      value={editingCompany.max_users}
                      onChange={(e) => setEditingCompany({ ...editingCompany, max_users: parseInt(e.target.value) || 50 })}
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="edit-company-max-users"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Logo URL</Label>
                    <Input
                      value={editingCompany.logo_url}
                      onChange={(e) => setEditingCompany({ ...editingCompany, logo_url: e.target.value })}
                      placeholder="https://..."
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="edit-company-logo"
                    />
                  </div>
                  
                  <div className="col-span-2 border-t border-slate-700 pt-4">
                    <p className="text-slate-300 font-medium mb-3">Branding Colors</p>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Primary Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={editingCompany.primary_color}
                        onChange={(e) => setEditingCompany({ ...editingCompany, primary_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={editingCompany.primary_color}
                        onChange={(e) => setEditingCompany({ ...editingCompany, primary_color: e.target.value })}
                        className="bg-slate-900 border-slate-600 text-white flex-1"
                        data-testid="edit-company-primary-color"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400">Secondary Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={editingCompany.secondary_color}
                        onChange={(e) => setEditingCompany({ ...editingCompany, secondary_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={editingCompany.secondary_color}
                        onChange={(e) => setEditingCompany({ ...editingCompany, secondary_color: e.target.value })}
                        className="bg-slate-900 border-slate-600 text-white flex-1"
                        data-testid="edit-company-secondary-color"
                      />
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="col-span-2 border-t border-slate-700 pt-4">
                    <p className="text-slate-300 font-medium mb-3">Preview</p>
                    <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-sm">
                      <div 
                        className="w-12 h-12 rounded-sm flex items-center justify-center text-white font-bold text-xl"
                        style={{ backgroundColor: editingCompany.primary_color }}
                      >
                        {editingCompany.name?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div>
                        <p className="text-white font-medium">{editingCompany.name || 'Company Name'}</p>
                        <p className="text-slate-400 text-sm">Plan: {editingCompany.plan} | Max: {editingCompany.max_users} users</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="btn-secondary">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-company-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Billing Details Dialog */}
        <Dialog open={billingDetailsOpen} onOpenChange={(open) => {
          setBillingDetailsOpen(open);
          if (!open) {
            setBillingDetails(null);
            setSelectedBillingCompany(null);
            setBillingTab('overview');
          }
        }}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-500" />
                Billing Details - {selectedBillingCompany?.company_name}
              </DialogTitle>
            </DialogHeader>
            
            {loadingBillingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : billingDetails ? (
              <div className="space-y-4">
                {/* Tabs */}
                <div className="flex gap-2 border-b border-slate-700 pb-2">
                  <button
                    onClick={() => setBillingTab('overview')}
                    className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
                      billingTab === 'overview' 
                        ? 'bg-slate-700 text-amber-500' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <BarChart3 className="w-4 h-4 inline mr-1" />
                    Overview
                  </button>
                  <button
                    onClick={() => setBillingTab('invoices')}
                    className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
                      billingTab === 'invoices' 
                        ? 'bg-slate-700 text-amber-500' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <FileText className="w-4 h-4 inline mr-1" />
                    Invoices ({billingDetails.invoices?.length || 0})
                  </button>
                  <button
                    onClick={() => setBillingTab('transactions')}
                    className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
                      billingTab === 'transactions' 
                        ? 'bg-slate-700 text-amber-500' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <CreditCard className="w-4 h-4 inline mr-1" />
                    Transactions ({billingDetails.transactions?.length || 0})
                  </button>
                  <button
                    onClick={() => setBillingTab('settings')}
                    className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
                      billingTab === 'settings' 
                        ? 'bg-slate-700 text-amber-500' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Settings className="w-4 h-4 inline mr-1" />
                    Settings
                  </button>
                </div>

                {/* Overview Tab */}
                {billingTab === 'overview' && (
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-slate-900 p-3 rounded-lg">
                        <p className="text-slate-400 text-xs uppercase">Plan</p>
                        <p className="text-lg font-bold text-white">{billingDetails.billing?.plan_name}</p>
                        <p className="text-green-400 text-sm">${billingDetails.billing?.price}/mo</p>
                      </div>
                      <div className="bg-slate-900 p-3 rounded-lg">
                        <p className="text-slate-400 text-xs uppercase">Status</p>
                        <p className={`text-lg font-bold ${
                          billingDetails.billing?.status === 'active' ? 'text-green-400' :
                          billingDetails.billing?.status === 'trial' ? 'text-amber-400' :
                          'text-slate-400'
                        }`}>
                          {billingDetails.billing?.status?.toUpperCase()}
                        </p>
                        {billingDetails.billing?.is_comped && (
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">COMPED</span>
                        )}
                      </div>
                      <div className="bg-slate-900 p-3 rounded-lg">
                        <p className="text-slate-400 text-xs uppercase">Total Revenue</p>
                        <p className="text-lg font-bold text-green-400">${billingDetails.financials?.total_revenue?.toFixed(2) || '0.00'}</p>
                        <p className="text-slate-500 text-xs">{billingDetails.financials?.transaction_count || 0} transactions</p>
                      </div>
                      <div className="bg-slate-900 p-3 rounded-lg">
                        <p className="text-slate-400 text-xs uppercase">Usage</p>
                        <p className="text-lg font-bold text-white">
                          {billingDetails.usage?.users}/{billingDetails.usage?.max_users === -1 ? '∞' : billingDetails.usage?.max_users}
                        </p>
                        <p className="text-slate-500 text-xs">users</p>
                      </div>
                    </div>

                    {/* Company & Admin Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-slate-900 p-4 rounded-lg">
                        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          Company Information
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Name:</span>
                            <span className="text-white">{billingDetails.company?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Slug:</span>
                            <code className="text-cyan-400">{billingDetails.company?.slug}</code>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Created:</span>
                            <span className="text-white">{billingDetails.company?.created_at ? new Date(billingDetails.company.created_at).toLocaleDateString() : '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Work Orders:</span>
                            <span className="text-white">{billingDetails.usage?.work_orders || 0}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-900 p-4 rounded-lg">
                        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          Admin Contact
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Name:</span>
                            <span className="text-white">{billingDetails.admin?.name || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Email:</span>
                            <span className="text-white">{billingDetails.admin?.email || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Billing Period Info */}
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        Billing Period
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-400 block">Period Start:</span>
                          <span className="text-white">{billingDetails.billing?.current_period_start ? new Date(billingDetails.billing.current_period_start).toLocaleDateString() : '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Period End:</span>
                          <span className="text-white">{billingDetails.billing?.current_period_end ? new Date(billingDetails.billing.current_period_end).toLocaleDateString() : '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Trial Ends:</span>
                          <span className="text-white">{billingDetails.billing?.trial_ends_at ? new Date(billingDetails.billing.trial_ends_at).toLocaleDateString() : '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Stripe Customer:</span>
                          <span className="text-white font-mono text-xs">{billingDetails.billing?.stripe_customer_id || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setCreditDialogOpen(true)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <PlusCircle className="w-4 h-4 mr-2" />
                        Add Credit
                      </Button>
                      <Button
                        onClick={() => handleToggleComped(!billingDetails.billing?.is_comped)}
                        variant="outline"
                        className="btn-secondary"
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        {billingDetails.billing?.is_comped ? 'Remove Comp' : 'Mark as Comped'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Invoices Tab */}
                {billingTab === 'invoices' && (
                  <div className="space-y-3">
                    {billingDetails.invoices?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-700">
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Invoice ID</th>
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Period</th>
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Plan</th>
                              <th className="text-right py-2 px-2 text-slate-400 text-xs uppercase">Amount</th>
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Status</th>
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Paid At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {billingDetails.invoices.map((invoice) => (
                              <tr key={invoice.id} className="border-b border-slate-700/50">
                                <td className="py-2 px-2">
                                  <code className="text-cyan-400 text-xs">{invoice.id.slice(0, 8)}...</code>
                                </td>
                                <td className="py-2 px-2 text-slate-300 text-sm">
                                  {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                                </td>
                                <td className="py-2 px-2">
                                  <span className="text-white text-sm uppercase">{invoice.plan}</span>
                                </td>
                                <td className="py-2 px-2 text-right text-green-400 font-mono">
                                  ${invoice.amount.toFixed(2)}
                                </td>
                                <td className="py-2 px-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                                    invoice.status === 'unpaid' ? 'bg-red-500/20 text-red-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>
                                    {invoice.status.toUpperCase()}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-slate-400 text-sm">
                                  {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No invoices yet</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Transactions Tab */}
                {billingTab === 'transactions' && (
                  <div className="space-y-3">
                    {billingDetails.transactions?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-700">
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">ID</th>
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Date</th>
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Description</th>
                              <th className="text-right py-2 px-2 text-slate-400 text-xs uppercase">Amount</th>
                              <th className="text-left py-2 px-2 text-slate-400 text-xs uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {billingDetails.transactions.map((tx) => (
                              <tr key={tx.id} className="border-b border-slate-700/50">
                                <td className="py-2 px-2">
                                  <code className="text-cyan-400 text-xs">{tx.id.slice(0, 8)}...</code>
                                </td>
                                <td className="py-2 px-2 text-slate-300 text-sm">
                                  {new Date(tx.created_at).toLocaleDateString()}
                                </td>
                                <td className="py-2 px-2 text-white text-sm">
                                  {tx.description}
                                </td>
                                <td className="py-2 px-2 text-right font-mono">
                                  <span className={tx.is_credit ? 'text-green-400' : 'text-white'}>
                                    {tx.is_credit ? '+' : ''}${tx.amount.toFixed(2)}
                                  </span>
                                </td>
                                <td className="py-2 px-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    tx.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                    tx.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                    tx.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>
                                    {tx.status.toUpperCase()}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No transactions yet</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Settings Tab */}
                {billingTab === 'settings' && (
                  <div className="space-y-4">
                    {/* Change Plan */}
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-slate-400" />
                        Subscription Plan
                      </h3>
                      <div className="flex items-center gap-4">
                        <select
                          value={billingDetails.billing?.plan || 'trial'}
                          onChange={(e) => {
                            handleUpdatePlan(selectedBillingCompany.company_id, e.target.value);
                            fetchBillingDetails(selectedBillingCompany.company_id);
                          }}
                          className="bg-slate-800 border border-slate-600 text-white rounded px-3 py-2"
                        >
                          <option value="free">Free (Complimentary)</option>
                          <option value="trial">Trial (14 days)</option>
                          <option value="basic">Basic (not in checkout catalog)</option>
                          <option value="pro">Pro - $29/mo</option>
                          <option value="enterprise">Enterprise - Contact us</option>
                        </select>
                        <div className="text-slate-400 text-sm">
                          Current: <span className="text-white font-medium">{billingDetails.billing?.plan_name}</span>
                        </div>
                      </div>
                    </div>

                    {/* Billing Notes */}
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        Internal Notes
                      </h3>
                      <textarea
                        value={billingNotes}
                        onChange={(e) => setBillingNotes(e.target.value)}
                        placeholder="Add internal notes about this account (not visible to customer)..."
                        className="w-full h-24 bg-slate-800 border border-slate-600 text-white rounded px-3 py-2 resize-none"
                      />
                      <div className="mt-2 flex justify-end">
                        <Button
                          onClick={handleSaveBillingNotes}
                          disabled={savingBillingNotes}
                          className="btn-primary"
                        >
                          {savingBillingNotes ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Notes
                        </Button>
                      </div>
                    </div>

                    {/* Danger Zone */}
                    <div className="bg-red-900/20 border border-red-600/30 p-4 rounded-lg">
                      <h3 className="text-red-400 font-medium mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Danger Zone
                      </h3>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">Cancel Subscription</p>
                          <p className="text-slate-400 text-sm">Move account back to trial status</p>
                        </div>
                        <Button
                          onClick={() => {
                            handleUpdatePlan(selectedBillingCompany.company_id, 'trial');
                            fetchBillingDetails(selectedBillingCompany.company_id);
                          }}
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-600/20"
                        >
                          Cancel Plan
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Failed to load billing details</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Credit Dialog */}
        <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <PlusCircle className="w-5 h-5 text-green-500" />
                Add Credit
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400">Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="0.00"
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400">Description</Label>
                <Input
                  value={creditDescription}
                  onChange={(e) => setCreditDescription(e.target.value)}
                  placeholder="e.g., Promotional credit, refund, etc."
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setCreditDialogOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddCredit}
                  disabled={addingCredit || !creditAmount || !creditDescription}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {addingCredit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlusCircle className="w-4 h-4 mr-2" />}
                  Add Credit
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SuperAdminPage;
